import { useLang } from "@/context/LanguageContext";
import { Globe, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function LanguageSelector({ compact = false }) {
  const { lang, globalLang, pageLang, changeLang, setPageLang, languages } = useLang();
  // Only languages actually switched on by the admin (exclude admin drafts).
  const visible = languages.filter((l) => !l.draft);
  const current = visible.find((l) => l.code === lang) || visible[0];

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        data-testid="language-selector-trigger"
        aria-label="Select language"
        className="flex items-center gap-1.5 text-sm text-ink-2 transition-colors hover:text-saffron focus:outline-none"
      >
        <Globe className="h-5 w-5 md:h-4 md:w-4" />
        {!compact && <span className="font-deva-body">{current?.native || current?.label}</span>}
        {!compact && pageLang && <span className="ml-0.5 rounded-sm bg-saffron/15 px-1 text-[9px] font-semibold uppercase tracking-wide text-saffron">page</span>}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="z-[70] max-h-[70vh] min-w-[12rem] overflow-y-auto rounded-none border-line bg-surface shadow-lg">
        {/* Tier 1 — site-wide default language */}
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-ink-3">Site language</DropdownMenuLabel>
        {visible.map((l) => (
          <DropdownMenuItem
            key={`g-${l.code}`}
            data-testid={`language-option-${l.code}`}
            onSelect={() => changeLang(l.code)}
            className="flex cursor-pointer items-center justify-between font-deva-body text-ink-2 focus:bg-surface-alt focus:text-ink"
          >
            <span className={l.code === globalLang ? "text-saffron" : ""}>
              {l.native || l.label}
              {l.draft && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-gold">draft</span>}
            </span>
            {l.code === globalLang && <Check className="h-3.5 w-3.5 text-saffron" />}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator className="bg-line" />

        {/* Tier 2 — override the language for THIS page only */}
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-ink-3">This page only</DropdownMenuLabel>
        <DropdownMenuItem
          data-testid="language-page-default"
          onSelect={() => setPageLang(null)}
          className="flex cursor-pointer items-center justify-between font-deva-body text-ink-2 focus:bg-surface-alt focus:text-ink"
        >
          <span className={!pageLang ? "text-saffron" : ""}>Use site default</span>
          {!pageLang && <Check className="h-3.5 w-3.5 text-saffron" />}
        </DropdownMenuItem>
        {visible.map((l) => (
          <DropdownMenuItem
            key={`p-${l.code}`}
            data-testid={`language-page-option-${l.code}`}
            onSelect={() => setPageLang(l.code)}
            className="flex cursor-pointer items-center justify-between font-deva-body text-ink-2 focus:bg-surface-alt focus:text-ink"
          >
            <span className={l.code === pageLang ? "text-saffron" : ""}>{l.native || l.label}</span>
            {l.code === pageLang && <Check className="h-3.5 w-3.5 text-saffron" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
